import {
  MutationFunctionOptions,
  MutationHookOptions,
  MutationTuple,
} from '@apollo/client';
import { useCallback, useEffect } from 'react';
import {
  useActiveWorkspace,
  useWorkspaceHelpers,
} from '../../store/workspace/workspace.hooks';
import { usePrevious } from '../../util/hooks';
import { useLogout } from '../auth.hooks';
import {
  Exact,
  UpdateTenantInput,
  UpdateTenantMutation,
  UpdateTenantMutationVariables,
  useGetTenantQuery,
  useUpdateTenantMutation,
} from '../graphql/generated';

export const useUpdateTenant = (
  baseOptions?: MutationHookOptions<
    UpdateTenantMutation,
    UpdateTenantMutationVariables
  >
): MutationTuple<UpdateTenantMutation, UpdateTenantMutationVariables> => {
  const [updateTenant, ...restMutationResult] =
    useUpdateTenantMutation(baseOptions);
  const workspace = useActiveWorkspace();
  const { upsertWorkspace } = useWorkspaceHelpers();
  const updateTenantWrapper = useCallback(
    async (
      options?:
        | MutationFunctionOptions<
            UpdateTenantMutation,
            Exact<{
              input: UpdateTenantInput;
            }>
          >
        | undefined
    ) => {
      const result = await updateTenant(options);
      if (workspace && result?.data) {
        const { updateTenant: tenant } = result.data;
        await upsertWorkspace({
          ...workspace,
          tenant,
        });
      }
      return result;
    },
    [workspace, upsertWorkspace, updateTenant]
  );
  return [updateTenantWrapper, ...restMutationResult];
};

export const useLogoutIfTenantDeactivated = () => {
  const logout = useLogout();
  const { data: tenantData } = useGetTenantQuery({
    fetchPolicy: 'cache-only',
  });

  const tenant = tenantData?.getTenant;
  const prevActive = usePrevious(tenant?.active);

  useEffect(() => {
    if (prevActive === true && tenant?.active === false) {
      logout();
    }
  }, [logout, prevActive, tenant?.active]);
};
